import { key } from './geometry';
import type { ObstacleMap, Point, Terrain } from './types';

export interface Carve {
  obstacles: ObstacleMap;
  /** Paint a cell. Out of bounds is ignored, and the first paint wins. */
  put: (r: number, c: number, t: Terrain) => void;
  /** What stands on a cell, or `undefined` for open land. */
  at: (r: number, c: number) => Terrain | undefined;
  /** Is this cell open land a tower could stand on? */
  free: (p: Point) => boolean;
}

/**
 * Start a blank board.
 *
 * First paint wins, deliberately: a shape draws its load-bearing channel or
 * wall first and scatters flourishes afterwards, so the flourishes can never
 * silently close the pass the layout depends on.
 */
export function beginCarve(size: number): Carve {
  const obstacles: ObstacleMap = {};
  const inside = (r: number, c: number): boolean => r >= 0 && r < size && c >= 0 && c < size;
  return {
    obstacles,
    put(r, c, t) {
      if (inside(r, c) && !obstacles[r + ',' + c]) obstacles[r + ',' + c] = t;
    },
    at(r, c) {
      return obstacles[r + ',' + c];
    },
    free(p) {
      return inside(p.r, p.c) && !obstacles[p.r + ',' + p.c];
    },
  };
}

/**
 * Every patch of open land that touches edge to edge, largest patch first.
 *
 * Diagonal neighbours are separate patches on purpose: an island is what the
 * eye reads as one piece of ground, and two blobs meeting at a corner read as
 * two.
 */
export function landRegions(obstacles: ObstacleMap, size: number): Point[][] {
  const seen = new Set<string>();
  const regions: Point[][] = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const start = { r, c };
      if (obstacles[key(start)] || seen.has(key(start))) continue;
      const region: Point[] = [start];
      seen.add(key(start));
      for (let i = 0; i < region.length; i++) {
        const cur = region[i];
        for (const [dr, dc] of [
          [-1, 0],
          [1, 0],
          [0, -1],
          [0, 1],
        ]) {
          const p = { r: cur.r + dr, c: cur.c + dc };
          if (p.r < 0 || p.r >= size || p.c < 0 || p.c >= size) continue;
          if (obstacles[key(p)] || seen.has(key(p))) continue;
          seen.add(key(p));
          region.push(p);
        }
      }
      regions.push(region);
    }
  }
  // Stable: bigger first, and scan order settles ties, so the "largest island"
  // is the same island on every device.
  return regions.sort((a, b) => b.length - a.length);
}

/**
 * The width of every strait: each run of water walled by land on both sides,
 * along rows and columns alike.
 *
 * Water that runs off the board is open sea rather than a strait and is left
 * out. What comes back is exactly the span rule's reading of the map — a 1 is
 * crossable, a 2 is not — which is how a carver checks that a channel it drew
 * one wide did not end up two wide by accident.
 */
export function straitWidths(obstacles: ObstacleMap, size: number): number[] {
  const widths: number[] = [];
  const scan = (cell: (i: number, j: number) => Point): void => {
    for (let i = 0; i < size; i++) {
      let run = 0;
      let walled = false;
      for (let j = 0; j < size; j++) {
        if (obstacles[key(cell(i, j))] === 'water') {
          run++;
        } else {
          if (run && walled) widths.push(run);
          run = 0;
          walled = true;
        }
      }
    }
  };
  scan((i, j) => ({ r: i, c: j }));
  scan((i, j) => ({ r: j, c: i }));
  return widths;
}
